import { z } from 'zod'

import { InventoryType } from '../constants/inventory'
import { BookingPaymentType } from '../constants/booking'
import { feeSchema } from './pricing'
import { dbEntitySchema, listQueryZodFields } from './system'
import { imageSchema } from './image'

export const productAvailabilitySchema = z.object({
  days: z.array(z.number().int().min(0).max(6)).default([]),
  startDate: z.coerce.date().nullish(),
  endDate: z.coerce.date().nullish(),
  blackoutDates: z.array(z.coerce.date()).nullish(),
})

export const productPriceOverrideSchema = z.object({
  date: z.coerce.date(),
  price: z.number().min(0),
})

export const productSellingWindowSchema = z.object({
  startMinutes: z.number().int().min(0).max(1799).nullish(),
  endMinutes: z.number().int().min(0).max(1799).nullish(),
})

export const productPricingOverridesSchema = z.object({
  taxRate: z.number().min(0).max(1).nullish(),
  serviceChargeRate: z.number().min(0).max(1).nullish(),
  fees: z.array(feeSchema).nullish(),
  userPaysProcessing: z.boolean().nullish(),
  paymentType: z.nativeEnum(BookingPaymentType).nullish(),
  termsAndConditions: z.string().nullish(),
})

export const productPricingPolicySchema = z.object({
  basePrice: z.number().min(0),
  priceOverrides: z.array(productPriceOverrideSchema).nullish(),
  taxRate: z.number().min(0).max(1).nullish(),
  serviceChargeRate: z.number().min(0).max(1).nullish(),
  fees: z.array(feeSchema).nullish(),
  userPaysProcessing: z.boolean().nullish(),
  paymentType: z.nativeEnum(BookingPaymentType).nullish(),
  termsAndConditions: z.string().nullish(),
})

export const productPurchaseConfigSchema = z.object({
  partySize: z
    .object({
      guests: z.number().int().min(0).nullish(),
    })
    .nullish(),
  deposit: z
    .object({
      amount: z.number().min(0).nullish(),
    })
    .nullish(),
  limits: z
    .object({
      quantityTotal: z.number().int().min(0).nullish(),
      purchaseMin: z.number().int().min(0).nullish(),
      purchaseMax: z.number().int().min(0).nullish(),
    })
    .nullish(),
  availability: productAvailabilitySchema.nullish(),
  sellingWindow: productSellingWindowSchema.nullish(),
  syncQuantityBySection: z.boolean().nullish(),
})

export const productSchema = dbEntitySchema.extend({
  organization: z.string(),
  venue: z.string(),
  enable: z.boolean().nullish(),
  isInternal: z.boolean().nullish(),
  price: z.number().min(0),
  name: z.string().nullish(),
  type: z.nativeEnum(InventoryType),
  productCategoryId: z.string().nullish(),
  venueLayoutSection: z.string().nullish(),
  images: z.array(imageSchema).nullish(),
  description: z.string().nullish(),
  shortDescription: z.string().nullish(),
  pricingPolicy: productPricingPolicySchema.nullish(),
  purchaseConfig: productPurchaseConfigSchema.nullish(),
})

export const productListQuerySchema = z.object({
  ...listQueryZodFields,
  venueId: z.string().optional(),
  id: z.string().optional(),
  search: z.string().optional(),
  enable: z.boolean().optional(),
  excludeInternal: z.boolean().optional(),
  organizationId: z.string().optional(),
  type: z
    .nativeEnum(InventoryType)
    .or(z.array(z.nativeEnum(InventoryType)))
    .optional(),
  productCategoryId: z.string().or(z.array(z.string())).optional(),
})

export const productListResponseSchema = z.object({
  data: z.array(productSchema),
})

export const productCreateArgsSchema = productSchema
  .omit({
    _id: true,
    id: true,
    deleted: true,
    created: true,
    modified: true,
    organization: true,
    venue: true,
    venueLayoutSection: true,
    type: true,
  })
  .partial()
  .extend({
    venueId: z.string(),
    venueLayoutSectionId: z.string().optional(),
    type: z.nativeEnum(InventoryType).optional(),
    price: z.number().min(0).optional(),
    pricingPolicy: productPricingPolicySchema.partial().optional(),
    purchaseConfig: productPurchaseConfigSchema
      .extend({
        availability: productAvailabilitySchema.partial().nullish(),
        sellingWindow: productSellingWindowSchema.partial().nullish(),
      })
      .partial()
      .optional(),
    productCategoryId: z.string().optional(),
  })

export const productResolveForDateArgsSchema = z.object({
  productId: z.string(),
  date: z.coerce.date(),
})

export const productSnapshotSchema = z.object({
  productId: z.string(),
  venueId: z.string(),
  date: z.coerce.date(),
  type: z.nativeEnum(InventoryType),
  productCategoryId: z.string().nullish(),
  name: z.string().nullish(),
  location: z.string().nullish(),
  description: z.string().nullish(),
  image: z.string().nullish(),
  images: z.array(imageSchema).nullish(),
  guests: z.number().min(0).nullish(),
  price: z.number().min(0),
  basePrice: z.number().min(0),
  paymentType: z.nativeEnum(BookingPaymentType),
  purchaseMin: z.number().int().min(0).nullish(),
  purchaseMax: z.number().int().min(0).nullish(),
  taxRate: z.number().min(0).max(1).nullish(),
  serviceChargeRate: z.number().min(0).max(1).nullish(),
  tax: z.number().min(0).max(1).nullish(),
  tip: z.number().min(0).max(1).nullish(),
  userPaysProcessing: z.boolean().nullish(),
  depositAmount: z.number().min(0).nullish(),
  discountValue: z.number().min(0).nullish(),
  fees: z.array(feeSchema).nullish(),
  fee: z
    .object({
      fees: z.array(feeSchema),
      enable: z.boolean().nullish(),
    })
    .nullish(),
  termsAndConditions: z.string().nullish(),
  sellingStart: z.date().nullish(),
  sellingEnd: z.date().nullish(),
  quantityTotal: z.number().int().min(0),
})

export const productResolveForDateResponseSchema = z.object({
  productId: z.string(),
  venueId: z.string(),
  date: z.coerce.date(),
  quantityTotal: z.number().int().min(0),
  quantityReserved: z.number().int().min(0),
  quantitySold: z.number().int().min(0),
  availableQuantity: z.number().int().min(0),
  snapshot: productSnapshotSchema,
})

export const productAvailabilityRangeQuerySchema = z.object({
  startDate: z.coerce.date(),
  endDate: z.coerce.date(),
})

export const productAvailabilityRangeItemSchema = z.object({
  productId: z.string(),
  venueId: z.string(),
  date: z.coerce.date(),
  quantityTotal: z.number().int().min(0),
  quantityReserved: z.number().int().min(0),
  quantitySold: z.number().int().min(0),
  availableQuantity: z.number().int().min(0),
})

export const productAvailabilityRangeResponseSchema = z.object({
  data: z.array(productAvailabilityRangeItemSchema),
})

export const productUpdateByIdArgsSchema = productSchema
  .partial()
  .omit({
    _id: true,
    id: true,
    deleted: true,
    created: true,
    modified: true,
    organization: true,
    venue: true,
    venueLayoutSection: true,
  })
  .extend({
    venueLayoutSectionId: z.string().nullish(),
    pricingPolicy: productPricingPolicySchema.partial().nullish(),
    purchaseConfig: productPurchaseConfigSchema
      .extend({
        availability: productAvailabilitySchema.partial().nullish(),
        sellingWindow: productSellingWindowSchema.partial().nullish(),
      })
      .partial()
      .nullish(),
    productCategoryId: z.string().nullish(),
  })
